// Can 81 (the coding-jobs plan, Phase 1): the coding job card, the only place a coding job is approved (backend
// lib/gateCatalog.js HG-CODE-JOB-01, card capability coding-job-card-v1). It is built only from the call's own
// arguments, which the approval is bound to: the plain sentence first, then the repository, the change in full (the headline may be cut short), the WHOLE
// brief exactly as the job will see it, and the time limit; where it runs and how it lands. Its words are the World's
// (backend lib/codingJobs.js codingJobCard; codingJobCard.test.ts holds them equal). A call it cannot show exactly
// cannot be approved here.

export const START_CODING_JOB_TOOL = "neucleos_start_coding_job";
export const CODING_JOB_REPOSITORIES: Readonly<
  Record<string, { readonly github: string; readonly label: string }>
> = {
  practice: {
    github: "Polaris-Group-LLC/coding-jobs-practice",
    label: "the practice repository (coding-jobs-practice)",
  },
};
export const DEFAULT_TIME_LIMIT_MINUTES = 120;
export const WHERE_IT_RUNS =
  "On cleo-2, in its own copy of the repository, as one Claude Code session (Opus 5.5, medium effort) on Jason's Claude Code subscription. It sees only this brief: not this conversation and nothing Cleo read. Its commands are sealed off from the Keychain and from this Mac's other files and logins, though they can reach the internet; it cannot deploy, release, or change .github/workflows/ or the repository's own rules and checks.";
export const HOW_IT_LANDS =
  "It opens a pull request as the Neucleos Agents app, which shows this brief. It merges only after the repository's own checks pass and a separate review agrees the change is exactly the brief. You can stop it at any time; nothing is pushed after a stop.";
export const CODING_JOB_CARD_BLOCKED =
  "This card cannot show this coding job exactly, so it cannot be approved here.";

export interface CodingJobCard {
  readonly headline: string;
  readonly rows: ReadonlyArray<readonly [label: string, text: string]>;
  readonly blocked: string | null;
}

/** "2 hours", "1 hour 30 minutes", "45 minutes" (backend lib/codingJobs.js durationWords). */
export function durationWords(minutes: number): string {
  const whole = Math.max(0, Math.round(minutes));
  const hours = Math.floor(whole / 60);
  const rest = whole % 60;
  const h = hours === 0 ? "" : `${hours} hour${hours === 1 ? "" : "s"}`;
  const m = rest === 0 ? "" : `${rest} minute${rest === 1 ? "" : "s"}`;
  return [h, m].filter(Boolean).join(" ") || "0 minutes";
}

const flat = (text: string) => text.replace(/\s+/g, " ").trim();
const normalBrief = (text: string) =>
  text
    .replace(/\r\n?/g, "\n")
    .replace(/[ \t]+\n/g, "\n")
    .trim();

/** The card for one start call, or a blocked card when the call is not exactly a job this card can show. */
export function codingJobCard(record: Record<string, unknown>): CodingJobCard {
  const allowed = new Set(["repository", "title", "brief", "timeLimitMinutes"]);
  const repository =
    typeof record["repository"] === "string"
      ? CODING_JOB_REPOSITORIES[record["repository"]]
      : undefined;
  const title = typeof record["title"] === "string" ? flat(record["title"]) : "";
  const brief = typeof record["brief"] === "string" ? normalBrief(record["brief"]) : "";
  const limit = record["timeLimitMinutes"] ?? DEFAULT_TIME_LIMIT_MINUTES;
  if (
    Object.keys(record).some((key) => !allowed.has(key)) ||
    repository === undefined ||
    title === "" ||
    brief === "" ||
    typeof limit !== "number" ||
    !Number.isInteger(limit)
  ) {
    return {
      headline: "Start a coding job",
      rows: [["Why you cannot approve it here", CODING_JOB_CARD_BLOCKED]],
      blocked: CODING_JOB_CARD_BLOCKED,
    };
  }
  return {
    headline: `Start a coding job on ${repository.label}: ${title}`,
    rows: [
      ["Repository", `${repository.label}, ${repository.github}`],
      ["Change", title],
      ["Brief", brief],
      ["Time limit", `${durationWords(limit)}. It is stopped then, whatever it has done.`],
      ["Where it runs", WHERE_IT_RUNS],
      ["How it lands", HOW_IT_LANDS],
    ],
    blocked: null,
  };
}
